import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Button,
  Box,
  AppBar,
  Paper,
  Toolbar,
  FormControlLabel,
  Chip,
  Select,
  Container,
  FormControl,
  Typography,
  InputAdornment,
  MenuItem,
  Grid,
  OutlinedInput,
  Checkbox,
  TextField,
  IconButton,
  Radio,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { EntityFormFields } from '../../../interfaces/Entity.interface.ts';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import { EntityActions } from '../../../Services/_actions/Entity/entity.actions.ts';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import HomeIcon from '@mui/icons-material/Home';
import EntityDropdown from '../../Shared/EntityDropdown/EntityDropdown.tsx';
import { entityFormsStateAtomFamily } from '../../../Services/_states/index.js';
import { useRecoilState } from 'recoil';

function CreateEntityForm() {
  const actions = EntityActions();
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const { state } = useLocation();
  const [rows, setRows] = useState<EntityFormFields[]>([]);
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);
  const [tableState, setTableState] = useRecoilState(entityFormsStateAtomFamily('uniqueId'));

  // const getEntityFormById = async (uid: any) => {
  //   let formFields = state?.form_fields || [];
  //   const hasDefault = formFields.some((field: any) => field?.isDefaultDisplay);
  //   if (!hasDefault && formFields.length > 0) {
  //     formFields[0].isDefaultDisplay = true;
  //   }
  //   setRows(formFields);
  //   const locked = formFields.map((field: any) => ({
  //     name: !!field.name,
  //     label: !!field.label,
  //     type: !!field.type,
  //   }));
  //   setLockedFields(locked);
  // };
  // const [entityFormData, setEntityFormData] = useState({});
  const getEntityFormData = async (entityFormId: string) => {
    try {
      const response = await actions.getFormById(entityFormId || '');
      if (response.status === 200) {
        // setEntityFormData(response?.data?.items[0] || {});
        let formFields = response?.data?.items?.form_fields || [];
        const hasDefault = formFields.some((field: any) => field?.isDefaultDisplay);
        if (!hasDefault && formFields.length > 0) {
          formFields[0].isDefaultDisplay = true;
        }
        setRows(formFields);
        const locked = formFields.map((field: any) => ({
          name: !!field.name,
          label: !!field.label,
          type: !!field.type,
        }));
        setLockedFields(locked);
      } else {
        showSnackBar('Failed to fetch entity form data.', 'error');
      }
    } catch (error) {
      showSnackBar('An error occurred while fetching entity form data.', 'error');
    }
  };

  const validateObjectsArray = async (objectsArray: any) => {
    const errors: FieldError[] = [];
    const nameSet = new Set();

    objectsArray.forEach((obj: any, index: number) => {
      const fieldError: FieldError = {};
      if (nameSet.has(obj.name)) {
        fieldError.name = `Duplicate name found: ${obj.name}`;
      } else if (obj.name.trim().length === 0) {
        fieldError.name = `Name field is required`;
      } else {
        nameSet.add(obj.name);
      }
      if (!obj.label || obj.label.trim().length === 0) {
        fieldError.label = `Label is required`;
      }
      if (obj.hasOptions && (!obj.options || obj.options.length === 0)) {
        fieldError.options = `At least one option is required`;
      }

      if (Object.keys(fieldError).length > 0) {
        errors[index] = fieldError;
      }
    });

    return errors;
  };

  const [lockedFields, setLockedFields] = useState<
    { name: boolean; label: boolean; type: boolean }[]
  >([]);

  const handleUpdateFormField = async (id: any) => {
    let data = rows;
    let validationErrors = await validateObjectsArray(data);

    setFieldErrors(validationErrors);

    if (!validationErrors?.length) {
      let form_fields = [...rows];
      try {
        let res = await actions.updateForm(id, { form_fields });
        if (res?.updated_at) {
          // showSnackBar('Form fildes update successfully!', 'success')
          getEntityFormData(state?._id);
        }
        if (res?.status === 200) {
          setTableState((prev) => ({
            ...prev,
            cache: {},
            totalCountCache: {},
          }));
          navigate(-1);
          showSnackBar('Form fields updated successfully!', 'success');
        }
      } catch (error) {
        showSnackBar(error?.toString(), 'error');
      }
    }
  };

  const handleAddNewField = () => {
    let newField = {
      name: '',
      type: 'text',
      label: '',
      hasOptions: false,
      options: [],
      placeholder: '',
      isRequired: false,
      // isVerify: false,
    };
    setRows([...rows, newField]);
  };

  useEffect(() => {
    if (!state || !state?.name) {
      navigate('/r/datasets');
    } else {
      getEntityFormData(state?._id);
    }
  }, []);

  const addOptionHandle = (index: number) => {
    let data = [...rows];
    let newOpt = data[index]['optionText'];
    let oldOpt = data[index]['options'];
    if (oldOpt && !oldOpt.includes(newOpt)) {
      data[index]['options'].push(data[index]['optionText']);
      data[index]['optionText'] = '';
      setRows(data);
    } else {
      // showSnackBar(`This option(${newOpt}) is already use in ${data[index]["type"]}`, 'error');
    }
  };

  const deleteOptionHandle = (index: number, opt_val: any) => {
    let data = [...rows];
    data[index]['options'];
    let arr = data[index]['options'];
    arr = arr.filter((e: any) => e !== opt_val);
    data[index]['options'] = arr;
    if (arr.length == 0) {
      data[index]['type'] = 'text';
      data[index]['hasOptions'] = false;
    }
    setRows(data);
  };

  const handleOnChangeValue = (
    index: number,
    event: { target: { name: string; value: string } }
  ) => {
    setFieldErrors((prev) => {
      const newErrors = [...prev];
      if (newErrors[index]) {
        delete newErrors[index][event.target.name as keyof FieldError];
        if (Object.keys(newErrors[index]).length === 0) {
          delete newErrors[index];
        }
      }
      return newErrors;
    });
    let data = [...rows];

    if (event.target.name === 'name') {
      let newName = event?.target?.value;
      newName = newName.toLowerCase();
      newName = newName.replace(/\s+/g, '_');
      const isDuplicate = data.some((obj, i) => obj.name === newName && i !== index);
      if (!isDuplicate) {
        data[index][event.target.name] = newName;
      } else {
        // showSnackBar(`This key name : ${newName} is already use in form. Please use another key name`, 'error');
      }
    } else {
      data[index][event.target.name] = event.target.value;
    }

    if (
      event.target.name == 'type' &&
      (event.target.value == 'radio' ||
        event.target.value == 'dropdown' ||
        event.target.value == 'checkbox')
    ) {
      data[index]['hasOptions'] = true;
    } else {
      if (event.target.name === 'label') {
        data[index]['placeholder'] = `Enter your ${event.target.value}`;
      }
    }
    if (
      event.target.name == 'type' &&
      event.target.value != 'radio' &&
      event.target.value != 'dropdown' &&
      event.target.value != 'checkbox'
    ) {
      data[index]['hasOptions'] = false;
      data[index]['optionText'] = '';
      data[index]['options'] = [];
    }
    setRows(data);
  };

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      href: '/',
    },
    { label: 'Datasets', href: '/r/datasets' },
    { label: 'Form' },
  ];

  return (
    <Box sx={{ width: '100%', minHeight: '90vh', backgroundColor: '#fff', padding: '15px' }}>
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(event, item: { label: string; href: string }) => {
          if (item?.href) {
            navigate(item?.href);
          }
        }}
      />
      <Box sx={{ flexGrow: 1, marginBottom: '10px' }}>
        <AppBar
          sx={{ backgroundColor: 'transparent', color: 'grey', boxShadow: 'none !important' }}
          position="static"
        >
          <Toolbar>
            <Typography color="inherit" variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Dataset form for {state?.name}
            </Typography>
            <Button
              onClick={() => handleUpdateFormField(state?._id)}
              variant="contained"
              size="small"
            >
              Save form
            </Button>
          </Toolbar>
        </AppBar>
      </Box>
      <Container maxWidth="lg" sx={{ m: 0 }}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Grid container spacing={2} sx={{ m: 0 }}>
            {rows &&
              rows.map((v: EntityFormFields, index: number) => (
                <Grid item xs={12} key={index} sx={{ paddingLeft: 0, paddingRight: '16px' }}>
                  <Box
                    sx={{
                      bgcolor: '#f5f5f5',
                      p: 2,
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      border: '1px solid #e5f3ff',
                      boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <Grid container spacing={2} alignItems="end">
                      <Grid item xs={12} sm={2}>
                        <FormControlLabel
                          control={
                            <Radio
                              checked={!!v?.isDefaultDisplay}
                              onChange={() => {
                                let data = rows.map((item, i) => ({
                                  ...item,
                                  isDefaultDisplay: i === index,
                                }));
                                setRows(data);
                              }}
                              value={index}
                              name="default-display-radio"
                              inputProps={{ 'aria-label': `Default Display ${index}` }}
                            />
                          }
                          label={
                            <span style={{ fontSize: '14px', color: 'grey' }}>Default Display</span>
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Typography variant="subtitle2">Field Name</Typography>
                        <OutlinedInput
                          fullWidth
                          value={v?.name}
                          name="name"
                          size="small"
                          onChange={(event: any) => handleOnChangeValue(index, event)}
                          //disabled={index === 0}
                          disabled={lockedFields[index]?.name}
                          // disabled={v?.name === 'phone_number'}
                          error={!!fieldErrors[index]?.name}
                        />
                        {fieldErrors[index]?.name && (
                          <Typography color="error" variant="caption">
                            {fieldErrors[index]?.name}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Typography variant="subtitle2">Label Name</Typography>
                        <OutlinedInput
                          fullWidth
                          value={v?.label}
                          name="label"
                          size="small"
                          onChange={(event: any) => handleOnChangeValue(index, event)}
                          error={!!fieldErrors[index]?.label}
                        />
                        {fieldErrors[index]?.label && (
                          <Typography color="error" variant="caption">
                            {fieldErrors[index]?.label}
                          </Typography>
                        )}
                      </Grid>
                      {!v?.hasOptions && (
                        <Grid item xs={12} sm={2}>
                          <Typography variant="subtitle2">Field Type</Typography>
                          <Select
                            size="small"
                            fullWidth
                            name="type"
                            value={v?.type}
                            disabled={index === 0}
                            // disabled={v?.name === 'phone_number'}
                            onChange={(event: any) => handleOnChangeValue(index, event)}
                          >
                            <MenuItem value="text">String</MenuItem>
                            <MenuItem value="number">Number</MenuItem>
                            <MenuItem value="password">Password</MenuItem>
                            <MenuItem value="phone">Phone</MenuItem>
                            <MenuItem value="email">Email</MenuItem>
                            <MenuItem value="date">Date</MenuItem>
                            <MenuItem value="checkbox">Checkbox</MenuItem>
                            <MenuItem value="radio">Radio</MenuItem>
                            <MenuItem value="dropdown">Dropdown</MenuItem>
                            <MenuItem value="textArea">TextArea</MenuItem>
                            <MenuItem value="image">Image</MenuItem>
                            <MenuItem value="video">Video</MenuItem>
                            <MenuItem value="document">Document</MenuItem>
                          </Select>
                        </Grid>
                      )}

                      {/* Add Options */}
                      {v?.hasOptions && (
                        <Grid item xs={12} sm={2}>
                          <Typography variant="subtitle2">Add Options</Typography>
                          <OutlinedInput
                            fullWidth
                            value={v?.optionText}
                            name="optionText"
                            size="small"
                            placeholder="Option Name"
                            onChange={(event: any) => handleOnChangeValue(index, event)}
                            error={!!fieldErrors[index]?.options}
                            endAdornment={
                              <InputAdornment position="end">
                                <span
                                  style={{
                                    fontSize: '28px',
                                    cursor: v?.optionText ? 'pointer' : 'auto',
                                  }}
                                  onClick={() => v?.optionText && addOptionHandle(index)}
                                >
                                  +
                                </span>
                              </InputAdornment>
                            }
                          />
                          {fieldErrors[index]?.options && (
                            <Typography color="error" variant="caption">
                              {fieldErrors[index]?.options}
                            </Typography>
                          )}
                        </Grid>
                      )}
                      {/* {v.type === "parentField" && (
                                            <Grid item xs={12} sm={3}>
                                                <EntityDropdown
                                                    value={v?.parentField}
                                                    onChange={(value: any) => {
                                                        let data = [...rows];
                                                        data[index]['parentField'] = { name: value?.name, _id: value?._id };
                                                        setRows(data);
                                                    }}
                                                    error={false}
                                                    label="Select Parent Field"
                                                    sx={{ mt: 1 }}
                                                />
                                            </Grid>
                                        )} */}

                      {/* Is Required Checkbox */}
                      <Grid item xs={12} sm={3}>
                        <FormControlLabel
                          name="isRequired"
                          control={
                            <Checkbox
                              checked={v?.isRequired}
                              onChange={() => {
                                let data = [...rows];
                                data[index]['isRequired'] = !v?.isRequired;
                                setRows(data);
                              }}
                              disabled={index === 0}
                            />
                          }
                          label={
                            <span style={{ fontSize: '14px', color: 'grey' }}>Is Required</span>
                          }
                        />
                        {/* <FormControlLabel
                          name="isVerify"
                          control={
                            <Checkbox
                              checked={v?.isVerify}
                              onChange={() => {
                                let data = [...rows];
                                data[index]['isVerify'] = !v?.isVerify;
                                setRows(data);
                              }}
                            />
                          }
                          label={
                            <span style={{ fontSize: '14px', color: 'grey' }}>Is Verify</span>
                          }
                        /> */}
                      </Grid>
                      <Grid item xs={12} sm={1} sx={{ pt: 0, pb: 1 }}>
                        {v?.name !== 'phone_number' && index !== 0 && (
                          <CloseIcon
                            onClick={() => {
                              let array = [...rows];
                              array.splice(index, 1);
                              setRows([...array]);
                            }}
                            sx={{ cursor: 'pointer', color: 'red' }}
                          />
                        )}
                      </Grid>
                    </Grid>
                    {v?.hasOptions && v?.options.length > 0 && (
                      <Box
                        sx={{
                          mt: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography>Field Options :</Typography>
                        {v?.options.map((opt: any, opt_ind: any) => (
                          <Chip
                            key={opt_ind}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <span>{opt}</span>
                                <CloseIcon
                                  sx={{ fontSize: '18px', cursor: 'pointer' }}
                                  onClick={() => deleteOptionHandle(index, opt)}
                                  color="error"
                                />
                              </Box>
                            }
                            size="small"
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
          </Grid>
          <Box mt={3} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
            <Button onClick={handleAddNewField} variant="contained" size="small">
              Add field
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
export default CreateEntityForm;
